import { Bell, CheckCheck, Inbox } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PATHS, hostApplicationDetailPath, reportDetailPath } from "@/constants/page-paths";
import { formatRelative } from "@/lib/format";
import type { AdminNotification } from "@/lib/realtime";
import { unreadCount, useAdminNotificationsStore, type StoredNotification } from "@/store";

/**
 * Xabar qaysi sahifaga olib boradi.
 *
 * Havolasiz xabar — yarim xabar: moderator "yangi shikoyat" degan
 * qatorni o'qib, uni menyudan qidirishga majbur bo'lardi. Ariza va
 * shikoyatda aniq kartochka bor, pul harakatida esa ro'yxatning o'zi:
 * bitta to'lovning alohida sahifasi yo'q va bo'lishi ham shart emas —
 * moderator u yerda ketma-ket bir nechtasini ko'radi.
 */
const destination = (notification: AdminNotification): string => {
  const id = notification.payload.entityId;

  switch (notification.type) {
    case "host_application_submitted":
      return id ? hostApplicationDetailPath(id) : PATHS.HOST_APPLICATIONS;
    case "report_submitted":
      return id ? reportDetailPath(id) : PATHS.REPORTS;
    case "withdrawal_created":
      return PATHS.WITHDRAWALS;
    case "payment_succeeded":
      return PATHS.PAYMENTS;
  }
};

/**
 * Navbat qo'ng'irog'i.
 *
 * SOCKETDAN KELADI, so'rov bilan emas: moderator ochiq turgan panelda
 * yangi arizani sahifani yangilamasdan ko'radi. Ilgari bu umuman
 * yo'q edi — navbatga tushgan ish faqat kimdir o'sha sahifani
 * ochganda ko'rinardi.
 *
 * QO'NG'IROQCHA HAMMAGA KO'RINADI va bu ataylab: ro'yxat bo'sh
 * bo'lsa u shunchaki bo'sh turadi. Uni ruxsatga bog'lash kerak emas,
 * chunki xabarning O'ZI ruxsatga bog'langan — backend faqat tegishli
 * xonaga yuboradi (`realtime.gateway`), ya'ni `payments.read` siz
 * moderatorga to'lov xabari umuman kelmaydi.
 */
export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const items = useAdminNotificationsStore(state => state.items);
  const markAllRead = useAdminNotificationsStore(state => state.markAllRead);
  const clear = useAdminNotificationsStore(state => state.clear);

  const unread = unreadCount(items);

  return (
    <DropdownMenu
      onOpenChange={open => {
        // Ochilgan ro'yxat — o'qilgan ro'yxat: har bir qatorni alohida
        // belgilash moderatorga ish qo'shardi va hech qanday savolga
        // javob bermasdi.
        if (open) markAllRead();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-full"
          aria-label={t("adminNotifications.title")}
        >
          <Bell className="size-4" />
          {unread > 0 && (
            <Badge
              className="absolute -top-0.5 -right-0.5 h-4 min-w-4 justify-center rounded-full px-1 text-[10px] tabular-nums"
              variant="destructive"
            >
              {unread > 9 ? "9+" : unread}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between gap-2">
          <span>{t("adminNotifications.title")}</span>
          {items.length > 0 && (
            <Button variant="ghost" size="sm" className="h-auto px-2 py-1" onClick={clear}>
              <CheckCheck className="size-3.5" />
              {t("adminNotifications.clear")}
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {items.length === 0 ? (
          <p className="text-muted-foreground flex items-start gap-2 p-3 text-xs">
            <Inbox className="mt-px size-3.5 shrink-0" />
            <span>{t("adminNotifications.empty")}</span>
          </p>
        ) : (
          <>
            <div className="max-h-80 overflow-y-auto">
              {items.map(item => (
                <Row key={item.id} item={item} language={i18n.language} />
              ))}
            </div>
            <DropdownMenuSeparator />
            <p className="text-muted-foreground p-2 text-[11px] leading-relaxed">
              {t("adminNotifications.sessionOnly")}
            </p>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Row({ item, language }: { item: StoredNotification; language: string }) {
  const { t } = useTranslation();

  const details = [
    item.payload.userName,
    item.payload.reportReason && t(`reports.reasons.${item.payload.reportReason}`, ""),
    item.payload.coffees !== undefined
      ? t("common.coffeeCount", { count: item.payload.coffees })
      : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <DropdownMenuItem asChild>
      <Link to={destination(item)} className="flex-col items-start gap-0.5">
        <span className="flex w-full items-center gap-2">
          {/* O'qilmagani — nuqta, qalin shrift emas: ro'yxatdagi to'rt
              qator qalin bo'lsa, ularning hech biri ajralib turmasdi. */}
          {!item.read && <span className="bg-primary size-1.5 shrink-0 rounded-full" />}
          <span className="truncate text-sm">{t(`adminNotifications.types.${item.type}`)}</span>
          <span className="text-muted-foreground ml-auto shrink-0 text-[11px]">
            {formatRelative(item.createdAt, language)}
          </span>
        </span>
        {details && <span className="text-muted-foreground truncate text-xs">{details}</span>}
      </Link>
    </DropdownMenuItem>
  );
}
